/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
import KatalMetricsDriverConsoleLogJson from "@amzn/katal-metrics/lib/driver/KatalMetricsDriverConsoleLogJson";
import KatalMetricsContext from "@amzn/katal-metrics/lib/KatalMetricsContext";
import KatalMetricsPublisher from "@amzn/katal-metrics/lib/KatalMetricsPublisher";
import KatalMetricsDriverSushi from "@amzn/katal-metrics-driver-sushi";
import { trackStructEvent } from "@snowplow/browser-tracker";

import { Logger } from "@/apis/Logger";
import {
  DEFAULT_STAGE_CONFIG,
  getStageConfig,
  STAGE,
} from "@/helpers/stageConfig";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

type MetricsInstance = {
  publisherInstance: KatalMetricsPublisher;
};

interface publishQueryMetricsBaseParams {
  metricsPublisherHandler: KatalMetricsPublisher;
  queryFunctionName: string;
}

interface publishQueryResultMetricsParams
  extends publishQueryMetricsBaseParams {
  numSuccess: number;
  numFailures: number;
}

interface publishQueryLatencyMetricsParams
  extends publishQueryMetricsBaseParams {
  elapsedTime: number;
}

/**
 * Error handler callback when attempts to publish metrics fail.
 * @param error The error from publishing metrics.
 */
export const errorHandler = (error: Error): void => {
  void Logger.error("Metrics collection error caught", error, {});
};

/**
 * Sushi Driver to publish the metrics to Sushi. Use it when you want to view your metrics on the Amazon Monitor
 * Portal
 */
const sushiDriver = new KatalMetricsDriverSushi.Builder()
  .withDomainRealm("prod", "USAmazon")
  .withErrorHandler(errorHandler)
  .build();

/**
 * Katal context which groups the published metrics with the given keys such as the name of the website and service.
 */
const metricsContext = new KatalMetricsContext.Builder()
  .withSite(
    getStageConfig()
      ? getStageConfig().snowplowAppId
      : DEFAULT_STAGE_CONFIG.snowplowAppId
  )
  .withServiceName("DayOneWizardWebsite")
  .build() as KatalMetricsContext;

/**
 * Metrics handler.
 *
 * usage:
 *
 * In the calling method, initialize the publisher handle with a description of what the method is, for example AppInit.
 * const metricsPublisherHandler = Metrics.publisherInstance.newChildActionPublisherForMethod("AppInit");
 *
 * To publish to PMET, use either
 * metricsPublisherHandler.publishCounterMonitor("identifierString", 1); or
 * metricsPublisherHandler.publishTimerMonitor("timerString", 100);
 *
 * See: https://katal.amazon.dev/for-developers/metrics/publishing/
 *
 * To view the metrics, make sure that you are using a sushi driver to publish your metrics to the right
 * Katal endpoint. The metrics can be accessed from the Amazon monitor portal with the link:
 * https://monitorportal.amazon.com/igraph?search=<snowplowAppId>
 *
 * Ex: Beta - https://monitorportal.amazon.com/igraph?search=ezo-day1-prelogin-beta
 */
export const Metrics: MetricsInstance = {
  publisherInstance: new KatalMetricsPublisher(
    STAGE === "DEV" ? new KatalMetricsDriverConsoleLogJson() : sushiDriver,
    errorHandler,
    metricsContext
  ),
};

/**
 * Publish the number of success and failed alias fetches through the Katal logger.
 * @param metricsPublisherHandler The Katal publisher to publish the metrics.
 * @param queryFunctionName The name of the query function.
 * @param numSuccess The number of success Amazon alias fetches.
 * @param numFailures The number of failure Amazon alias fetches.
 */
export const publishQueryResultMetrics = ({
  metricsPublisherHandler,
  queryFunctionName,
  numSuccess = 0,
  numFailures = 0,
}: publishQueryResultMetricsParams): void => {
  metricsPublisherHandler.publishCounterMonitor(
    `${queryFunctionName}Success`,
    numSuccess
  );
  metricsPublisherHandler.publishCounterMonitor(
    `${queryFunctionName}Failure`,
    numFailures
  );
};

/**
 * Publish the latency(ms) of an API through the Katal logger.
 * @param metricsPublisherHandler The Katal publisher to publish the metrics.
 * @param queryFunctionName The name of the query function.
 * @param elapsedTime The time spent in fetching/updating data from an API.
 */
export const publishQueryLatencyMetrics = ({
  metricsPublisherHandler,
  queryFunctionName,
  elapsedTime,
}: publishQueryLatencyMetricsParams): void => {
  metricsPublisherHandler.publishTimerMonitor(
    `${queryFunctionName}LatencyMs`,
    elapsedTime
  );
};

/**
 * Security Key Detection and Testing Metrics
 */
interface SecurityKeyTimingMetricsParams {
  elapsedTime: number;
  attemptNumber: number;
}

interface SecurityKeyStepCompleteMetricsParams {
  detectionAttempts: number;
  otpAttempts: number;
}

/**
 * Publish security key detection timing and success metrics using Snowplow.
 * @param params detection metrics parameters including timing and user context
 */
export const publishSecurityKeyDetectionMetrics = ({
  elapsedTime,
  attemptNumber,
}: SecurityKeyTimingMetricsParams): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: Day1EventAction.SecurityKeyDetected,
    value: Math.round(elapsedTime),
    property: `attempt-${attemptNumber}`,
  });

  void Logger.info("Security Key Detection Success", undefined, {
    elapsedTimeMs: elapsedTime,
    attemptNumber,
    metricType: "SecurityKeyDetection",
  });
};

/**
 * Publish security key OTP validation timing and success metrics using Snowplow.
 * @param params - OTP validation metrics parameters including timing and user context
 */
export const publishSecurityKeyOtpMetrics = ({
  elapsedTime,
  attemptNumber,
}: SecurityKeyTimingMetricsParams): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: Day1EventAction.SecurityKeyOtpValidated,
    value: Math.round(elapsedTime),
    property: `attempt-${attemptNumber}`,
  });

  void Logger.info("Security Key OTP Validation Success", undefined, {
    elapsedTimeMs: elapsedTime,
    attemptNumber,
    metricType: "SecurityKeyOtpValidation",
  });
};

/**
 * Publish security key step completion metrics using Snowplow.
 * @param params - Step completion metrics parameters including attempt counts
 */
export const publishSecurityKeyStepCompleteMetrics = ({
  detectionAttempts,
  otpAttempts,
}: SecurityKeyStepCompleteMetricsParams): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: Day1EventAction.InsertAndTestSecurityKeyStepComplete,
    value: 1,
  });

  void Logger.info("Security Key Detection Step Complete", undefined, {
    detectionAttempts,
    otpAttempts,
    totalAttempts: detectionAttempts + otpAttempts,
    metricType: "SecurityKeyStepComplete",
  });
};

interface SurveyAnswers {
  selectedReason: string;
  selectedDelivery: string;
  otherReasonDetails?: string;
}

interface SecurityKeyIssuesFormMetricsParams {
  surveyAnswers: SurveyAnswers;
  formCompletionTime: number;
}

interface SecurityKeyIssuesFormCanceledParams {
  partialData: SurveyAnswers;
}

interface MetricConfig {
  action: Day1EventAction;
  logMessage: string;
  metricType: string;
  value?: number;
  additionalData?: Record<string, unknown>;
}

/**
 * Publishes a metric for an on-demand VPN status check outcome on the
 * Connect VPN step (check runs on Next click in the manual flow and never
 * blocks progression — see NHE-6186).
 *
 * @param action - The VpnStatusCheck* Day1EventAction describing the outcome
 * @param additionalData - Optional additional data to include in logging
 */
export const publishVpnStatusCheckMetric = (
  action: Day1EventAction,
  additionalData?: Record<string, unknown>
): void => {
  trackStructEvent({
    category: Day1EventCategory.ConnectToVpn,
    action,
    value: 1,
  });

  void Logger.info("VPN status check outcome", undefined, {
    ...additionalData,
    action,
    metricType: "VpnStatusCheck",
  });
};

/**
 * Publishes a generic metric event using Snowplow tracking and Logger.
 *
 * @param config - Configuration object for the metric
 * @param config.action - The Day1EventAction to track
 * @param config.logMessage - Message to include in the log
 * @param config.metricType - Type of metric being tracked
 * @param config.value - Optional numeric value for the event (defaults to 1)
 * @param config.additionalData - Optional additional data to include in logging
 */
const publishGenericMetric = (config: MetricConfig): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: config.action,
    value: config.value ?? 1,
  });

  void Logger.info(config.logMessage, undefined, {
    ...config.additionalData,
    metricType: config.metricType,
  });
};

/**
 * Publish security key issue reported metric using Snowplow.
 * Tracks when user clicks "Report security key issue" button.
 */
export const publishSecurityKeyIssueReportedMetric = (): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: Day1EventAction.SecurityKeyIssueReported,
    value: 1,
  });

  void Logger.info("Security Key Issue Reported", undefined, {
    metricType: "SecurityKeyIssueReported",
  });
};

/**
 * Publish security key OTP error banner metric using Snowplow.
 * Tracks when no OTP generated for 30 seconds and error banner appears.
 */
export const publishSecurityKeyOtpErrorBannerMetric = (): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: Day1EventAction.SecurityKeyOtpErrorDisplayed,
    value: 1,
  });

  void Logger.info("Security Key OTP Error Banner Displayed", undefined, {
    metricType: "SecurityKeyOtpErrorBanner",
  });
};

/**
 * Publish security key unavailable clicked metrics using Snowplow.
 */
export const publishSecurityKeyUnavailableClickedMetrics = (): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyUnavailableClicked,
    logMessage: "Security Key Unavailable Button Clicked",
    metricType: "SecurityKeyUnavailableClicked",
  });
};

/**
 * Publish security key issues modal opened metrics using Snowplow.
 */
export const publishSecurityKeyIssuesModalOpenedMetrics = (): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyIssuesModalOpened,
    logMessage: "Security Key Issues Modal Opened",
    metricType: "SecurityKeyIssuesModalOpened",
  });
};

/**
 * Publish security key issues form submitted metrics using Snowplow.
 *
 * @param {Object} params - Form submission metrics parameters
 * @param {Object} params.surveyAnswers - Survey response data
 * @param {string} params.surveyAnswers.selectedReason - Selected reason for security key issue
 * @param {string} params.surveyAnswers.selectedDelivery - Selected delivery option
 * @param {string} [params.surveyAnswers.otherReasonDetails] - Optional additional details
 * @param {number} params.formCompletionTime - Time taken to complete form in milliseconds
 */
export const publishSecurityKeyIssuesFormSubmittedMetrics = ({
  surveyAnswers,
  formCompletionTime,
}: SecurityKeyIssuesFormMetricsParams): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyIssuesFormSubmitted,
    logMessage: "Security Key Issues Form Submitted",
    metricType: "SecurityKeyIssuesFormSubmitted",
    value: Math.round(formCompletionTime),
    additionalData: {
      surveyAnswers,
      formCompletionTimeMs: formCompletionTime,
    },
  });
};

/**
 * Publish security key issues chat support clicked metrics using Snowplow.
 */
export const publishSecurityKeyIssuesChatSupportClickedMetrics = (): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyIssuesChatSupportClicked,
    logMessage: "Security Key Issues Chat Support Clicked",
    metricType: "SecurityKeyIssuesChatSupportClicked",
  });
};

/**
 * Publish security key issues modal closed metrics using Snowplow.
 */
export const publishSecurityKeyIssuesModalClosedMetrics = (): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyIssuesModalClosed,
    logMessage: "Security Key Issues Modal Closed",
    metricType: "SecurityKeyIssuesModalClosed",
  });
};

/**
 * Publish security key issues form canceled metrics using Snowplow.
 *
 * @param {Object} params - Form cancellation metrics parameters
 * @param {SurveyAnswers} params.partialData - Partial survey response data captured before cancellation
 * @param {string} params.partialData.selectedReason - Selected reason for security key issue
 * @param {string} params.partialData.selectedDelivery - Selected delivery option
 * @param {string} [params.partialData.otherReasonDetails] - Optional additional details provided
 */
export const publishSecurityKeyIssuesFormCanceledMetrics = ({
  partialData,
}: SecurityKeyIssuesFormCanceledParams): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyIssuesFormCanceled,
    logMessage: "Security Key Issues Form Canceled",
    metricType: "SecurityKeyIssuesFormCanceled",
    additionalData: { partialData },
  });
};

/**
 * Publish security key step skipped metric using Snowplow.
 * Tracks when user clicks "Continue without testing" button.
 * @param isDetectionTimeout - true if skipped during detection timeout, false if during OTP timeout
 */
export const publishSecurityKeyStepSkippedMetric = (
  isDetectionTimeout = false
): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: isDetectionTimeout
      ? Day1EventAction.SecurityKeyDetectionSkipped
      : Day1EventAction.SecurityKeyOtpSkipped,
    value: 1,
  });

  void Logger.info("Security Key Step Skipped", undefined, {
    metricType: isDetectionTimeout
      ? "SecurityKeyDetectionSkipped"
      : "SecurityKeyOtpSkipped",
  });
};

/**
 * Publish insert and test security key step bypassed metric using Snowplow.
 * Tracks when the platform does not support the insert and test security key step.
 * @param os - Operating system information
 * @param userAgent - User agent string
 */
export const publishInsertAndTestSecurityKeyStepBypassedMetric = (
  os: string,
  userAgent: string
): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: Day1EventAction.InsertAndTestSecurityKeyStepBypassed,
    value: 1,
    property: JSON.stringify({ os, userAgent }),
  });

  void Logger.info("Insert and Test Security Key Step Bypassed", undefined, {
    os,
    userAgent,
    metricType: "InsertAndTestSecurityKeyStepBypassed",
  });
};

interface MultipleSecurityKeysDetectedParams {
  securityKeys: Array<{ vendorId: number; productId: number }>;
}

/**
 * Publish multiple security keys detected metric using Snowplow.
 * Tracks when more than one security key is detected during security key detection process.
 * Captures vendorId and productId information of all connected keys.
 *
 * @param params - Multiple security keys detection parameters
 * @param params.securityKeys - Array of detected security key device identifiers
 */
export const publishMultipleSecurityKeysDetectedMetric = ({
  securityKeys,
}: MultipleSecurityKeysDetectedParams): void => {
  trackStructEvent({
    category: Day1EventCategory.InsertAndTestSecurityKey,
    action: Day1EventAction.MultipleSecurityKeysDetected,
    value: securityKeys.length,
    property: JSON.stringify(securityKeys),
  });

  void Logger.info("Multiple Security Keys Detected", undefined, {
    securityKeys,
    totalKeysDetected: securityKeys.length,
    metricType: "MultipleSecurityKeysDetected",
  });
};

/**
 * Publish security key "not arrived" issue metric using Snowplow.
 * Tracks when user reports "My security key hasn't arrived" in Question 1 of the issues form upon submission.
 */
export const publishSecurityKeyNotArrivedMetric = (): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyIssuesFormQ1NotArrivedReported,
    logMessage: "Security Key Not Arrived Issue Reported",
    metricType: "SecurityKeyIssuesFormQ1NotArrivedReported",
  });
};

/**
 * Publish security key "not with me" issue metric using Snowplow.
 * Tracks when user reports "I received my security key, but it's not with me right now" in Question 1 of the issues form upon submission.
 */
export const publishSecurityKeyNotWithMeMetric = (): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyIssuesFormQ1NotWithMeReported,
    logMessage: "Security Key Not With Me Issue Reported",
    metricType: "SecurityKeyIssuesFormQ1NotWithMeReported",
  });
};

interface SecurityKeyOtherIssueParams {
  otherReasonDetails?: string;
}

/**
 * Publish security key "other" issue metric using Snowplow.
 * Tracks when user reports "Other, not listed" in Question 1 of the issues form upon submission.
 *
 * @param params - Other issue parameters
 * @param params.otherReasonDetails - Optional additional details provided by user for "other" reason
 */
export const publishSecurityKeyOtherIssueMetric = ({
  otherReasonDetails,
}: SecurityKeyOtherIssueParams): void => {
  publishGenericMetric({
    action: Day1EventAction.SecurityKeyIssuesFormQ1OtherIssueReported,
    logMessage: "Security Key Other Issue Reported",
    metricType: "SecurityKeyIssuesFormQ1OtherIssueReported",
    additionalData: {
      otherReasonDetails,
    },
  });
};

/**
 * How the ID still was produced (mirrors the recorder's IdImageCaptureSource):
 * - "auto": a frame qualified and was auto-selected.
 * - "fallback": nothing qualified, so the ~13s force-capture was used.
 * - "none": capture was enabled but no still was produced.
 */
export type IdStillCaptureOutcome = "auto" | "fallback" | "none";
export type IdStillDecision = "accepted" | "retried";

/**
 * Publish the outcome of automatic ID still selection for a manual-RIV
 * recording: whether a good frame was auto-selected, the fallback was used, or
 * no still was produced. Emitted once per ID recording.
 */
export const publishIdStillCaptureOutcome = (params: {
  documentType: string;
  outcome: IdStillCaptureOutcome;
}): void => {
  trackStructEvent({
    category: Day1EventCategory.IDCheckFull,
    action: Day1EventAction.IdStillCaptureOutcome,
    property: params.outcome,
    label: params.documentType,
    value: 1,
  });

  void Logger.info("ID still capture outcome", undefined, {
    documentType: params.documentType,
    outcome: params.outcome,
    metricType: "IdStillCaptureOutcome",
  });
};

/**
 * Publish the new hire's decision on the captured ID still — whether they
 * accepted it or retook the recording — along with how that still had been
 * produced, so accept/retry rates can be correlated with capture source.
 */
export const publishIdStillDecision = (params: {
  documentType: string;
  decision: IdStillDecision;
  source: IdStillCaptureOutcome;
}): void => {
  trackStructEvent({
    category: Day1EventCategory.IDCheckFull,
    action: Day1EventAction.IdStillDecision,
    property: params.decision,
    label: params.source,
    value: 1,
  });

  void Logger.info("ID still decision", undefined, {
    documentType: params.documentType,
    decision: params.decision,
    source: params.source,
    metricType: "IdStillDecision",
  });
};
